import { Autocomplete, TextField, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useCustomers } from '@/api/hooks'
import type { CustomerSummary } from '@/api/types'
import { formatMobile } from '@/components/common'

/**
 * Finds an existing customer by name, mobile, email or customer code.
 *
 * <p>The searching is the server's: it looks across all four fields, and the
 * list it returns is shown exactly as it comes. Filtering it again in the
 * browser is what made the obvious searches fail - the option label shows a
 * mobile grouped five and five, so typing the ten digits matched nothing, and a
 * customer code never appears in the label at all.</p>
 */
export function CustomerPicker({
  value,
  onChange,
  label = 'Customer',
  required,
  helperText,
  onTypedText,
}: {
  value: CustomerSummary | null
  onChange: (customer: CustomerSummary | null) => void
  label?: string
  required?: boolean
  helperText?: string
  /** What was typed, so a caller can offer to create whoever was not found. */
  onTypedText?: (text: string) => void
}) {
  const [typed, setTyped] = useState('')
  const [query, setQuery] = useState('')

  // Asked for a beat after typing stops rather than on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setQuery(typed.trim()), 250)
    return () => clearTimeout(timer)
  }, [typed])

  // A disabled customer is usually a spotted duplicate, kept for the orders
  // already against it - new business goes to whoever it was merged into,
  // not back to the record being retired.
  const { data, isFetching } = useCustomers({ q: query || undefined, enabled: true, size: 20 })
  const options = data?.content ?? []

  return (
    <Autocomplete
      options={options}
      value={value}
      loading={isFetching}
      getOptionLabel={(option) => `${option.name} · ${formatMobile(option.mobile)}`}
      filterOptions={(shown) => shown}
      isOptionEqualToValue={(option, chosen) => option.id === chosen.id}
      onInputChange={(_event, text, reason) => {
        // Choosing someone puts their label in the box; searching for that again
        // would be a wasted round trip.
        if (reason === 'reset') return
        setTyped(text)
        onTypedText?.(text)
      }}
      onChange={(_event, chosen) => onChange(chosen)}
      noOptionsText={
        typed.trim().length === 0
          ? 'Type a name, mobile, email or customer code'
          : 'Nobody found by that'
      }
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <span>
            {option.name}{' '}
            <Typography component="span" variant="caption" color="text.secondary">
              {formatMobile(option.mobile)}
              {option.customerCode ? ` · ${option.customerCode}` : ''}
            </Typography>
          </span>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          required={required}
          label={label}
          helperText={helperText ?? 'Search by name, mobile, email or customer code'}
        />
      )}
    />
  )
}
